import { useEffect } from 'react';

/**
 * Scroll-reveal engine for the landing home. Called once from Home.
 * Watches every `.cf-reveal` under `.cf-landing` and toggles `.is-in` when it
 * enters the viewport. A MutationObserver re-scans for async-rendered nodes
 * (e.g. live RecentMatches cards). Dependency-free; respects reduced-motion.
 */
const useScrollReveal = () => {
  useEffect(() => {
    const root = document.querySelector('.cf-landing');
    if (!root) return undefined;

    const reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      root.querySelectorAll('.cf-reveal').forEach((el) => el.classList.add('is-in'));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    const observeAll = (scope) =>
      scope.querySelectorAll('.cf-reveal:not(.is-in)').forEach((el) => io.observe(el));

    observeAll(root);

    const mo = new MutationObserver((mutations) => {
      mutations.forEach((m) =>
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.classList && node.classList.contains('cf-reveal')) io.observe(node);
          observeAll(node);
        })
      );
    });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);
};

export default useScrollReveal;
